/**
 * Segurados (P-SEG-1): lista, cadastro completo, bloqueio e as listas do cadastro.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3, e
 * docs/ADR/0029-segurado.md.
 */

import { query, type ListParams } from './comum';

import { api, requestPage } from '../client';

export type InsuredStatus = 'ativo' | 'bloqueado';
export type InsuredSector = 'publico' | 'privado';
export type PersonKind = 'pf' | 'pj';

export interface Insured {
  id: string;
  legal_name: string;
  document: string;
  person_kind: PersonKind;
  sector: InsuredSector;
  email: string;
  parent_insured: { id: string; label: string } | null;
  commercial_registry: boolean;
  pep_status: 'nao_verificado' | 'aprovado' | 'recusado';
  pep_checked_at: string | null;
  status: InsuredStatus;
  is_blocked: boolean;
  blocked_at: string | null;
  blocked_label: string;
  block_reason: string;
  document_unchecked: boolean;
  imported: boolean;
  created_label: string;
  created_at: string;
  updated_at: string;
}

export interface InsuredAddress {
  id: string;
  insured_id: string;
  label: string;
  zip_code: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  city_code: string;
  international: boolean;
  position: number;
  is_main: boolean;
  active: boolean;
  latitude_e6: number | null;
  longitude_e6: number | null;
  distance_m: number | null;
  radius_m: number | null;
  created_label: string;
  created_at: string;
  updated_at: string;
}

export interface InsuredContact {
  id: string;
  phone: string;
  is_main: boolean;
  created_at: string;
  updated_at: string;
}

export interface InsuredCnae {
  id: string;
  cnae_code: string;
  description: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export interface InsuredTradeName {
  id: string;
  name: string;
  is_main: boolean;
  created_at: string;
  updated_at: string;
}

export interface InsuredDetail extends Insured {
  addresses: InsuredAddress[];
  contacts: InsuredContact[];
  cnaes: InsuredCnae[];
  trade_names: InsuredTradeName[];
}

/** Endereco no pedido: o grupo comum de endereco mais os campos do OnPoint. */
export interface InsuredAddressInput {
  zip_code?: string;
  street?: string;
  number?: string;
  complement?: string;
  district?: string;
  city?: string;
  state?: string;
  city_code?: string;
  international?: boolean;
  position?: number;
  is_main?: boolean;
  active?: boolean;
}

export interface InsuredInput {
  legal_name?: string;
  document?: string;
  sector?: InsuredSector;
  email?: string;
  parent_insured_id?: string | null;
  commercial_registry?: boolean;
  address?: InsuredAddressInput;
}

export interface InsuredRegistrationCheck {
  document: string;
  person_kind: PersonKind;
  existing: Insured | null;
}

// --- Socios / Administradores e Historico de Alteracoes (P-SEG-2) -----------------

/** Papel da pessoa no quadro. Os valores do OnPoint estao a confirmar (SEG-093). */
export type InsuredPartnerRole = 'socio' | 'administrador' | 'representante';

/** Pessoa do quadro societario do segurado. */
export interface InsuredPartner {
  id: string;
  insured_id: string;
  /** Pessoa juridica do quadro de quem esta e socia. Nulo e socio direto. */
  parent_id: string | null;
  parent_label: string;
  /** Profundidade na arvore (Level do OnPoint): zero e socio direto. */
  level: number;
  role: InsuredPartnerRole;
  role_description: string;
  name: string;
  document: string;
  document_kind: 'cpf' | 'cnpj';
  is_company: boolean;
  /** Pontos-base inteiros (10.000 = 100%). Nulo e nao informada. */
  participation_bps: number | null;
  is_pep: boolean;
  is_majority: boolean;
  /** Documento aceito sem digito valido. So a carga de dados liga. */
  document_unchecked: boolean;
  source: 'consulta' | 'digitado' | 'importado';
  created_label: string;
  created_at: string;
  updated_at: string;
}

/**
 * Pessoa do quadro no pedido. Origem e documento nao conferido nao vao: sao da
 * consulta e da carga, e o servidor recusa o corpo que os traz.
 */
export interface InsuredPartnerInput {
  role?: InsuredPartnerRole;
  role_description?: string;
  name?: string;
  document?: string;
  participation_bps?: number | null;
  is_pep?: boolean;
  is_majority?: boolean;
  parent_id?: string | null;
}

/** Linha do cadastro que a alteracao mudou. */
export type InsuredChangeItemType =
  'segurado' | 'endereco' | 'telefone' | 'cnae' | 'nome_fantasia' | 'socio';

/** Uma escrita do cadastro do segurado, com o antes e o depois. */
export interface InsuredChangeEntry {
  id: string;
  action: string;
  actor_label: string;
  occurred_at: string;
  item_type: InsuredChangeItemType;
  /** No endereco, o LocationId do OnPoint. */
  item_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  /** Campos que mudaram, sem o identificador da linha. */
  changed: string[];
  changes: { field: string; before: unknown; after: unknown }[];
  /** Vazio quando quem le nao tem a leitura da auditoria. */
  ip: string;
  user_agent: string;
  origin_hidden: boolean;
  request_id: string;
}

export interface InsuredChangeParams {
  limit?: number;
  offset?: number;
  /** Comeco da acao, como `segurados.socio`. */
  action?: string;
  sort?: 'occurred_at' | '-occurred_at';
}

const base = '/api/v1/insureds';

export const insureds = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<Insured>(`${base}${query(params)}`, signal),
  get: (id: string, signal?: AbortSignal) =>
    api.get<InsuredDetail>(`${base}/${id}`, signal),
  create: (input: InsuredInput) => api.post<InsuredDetail>(base, input),
  update: (id: string, input: InsuredInput) =>
    api.patch<InsuredDetail>(`${base}/${id}`, input),
  checkRegistration: (document: string, signal?: AbortSignal) =>
    api.get<InsuredRegistrationCheck>(
      `${base}/registration-check${query({ document })}`,
      signal,
    ),
  block: (id: string, reason: string) =>
    api.post<Insured>(`${base}/${id}/block`, { reason }),
  unblock: (id: string) => api.post<Insured>(`${base}/${id}/unblock`, {}),

  addAddress: (id: string, input: InsuredAddressInput) =>
    api.post<InsuredAddress>(`${base}/${id}/addresses`, input),
  updateAddress: (id: string, addressId: string, input: InsuredAddressInput) =>
    api.patch<InsuredAddress>(`${base}/${id}/addresses/${addressId}`, input),
  removeAddress: (id: string, addressId: string) =>
    api.delete<void>(`${base}/${id}/addresses/${addressId}`),

  addContact: (id: string, input: { phone: string; is_main?: boolean }) =>
    api.post<InsuredContact>(`${base}/${id}/contacts`, input),
  updateContact: (
    id: string,
    contactId: string,
    input: { phone?: string; is_main?: boolean },
  ) => api.patch<InsuredContact>(`${base}/${id}/contacts/${contactId}`, input),
  removeContact: (id: string, contactId: string) =>
    api.delete<void>(`${base}/${id}/contacts/${contactId}`),

  addTradeName: (id: string, input: { name: string; is_main?: boolean }) =>
    api.post<InsuredTradeName>(`${base}/${id}/trade-names`, input),
  updateTradeName: (
    id: string,
    nameId: string,
    input: { name?: string; is_main?: boolean },
  ) => api.patch<InsuredTradeName>(`${base}/${id}/trade-names/${nameId}`, input),
  removeTradeName: (id: string, nameId: string) =>
    api.delete<void>(`${base}/${id}/trade-names/${nameId}`),

  addCnae: (id: string, input: { cnae_code: string; is_primary?: boolean }) =>
    api.post<InsuredCnae>(`${base}/${id}/cnaes`, input),
  updateCnae: (
    id: string,
    cnaeId: string,
    input: { cnae_code?: string; is_primary?: boolean },
  ) => api.patch<InsuredCnae>(`${base}/${id}/cnaes/${cnaeId}`, input),
  removeCnae: (id: string, cnaeId: string) =>
    api.delete<void>(`${base}/${id}/cnaes/${cnaeId}`),

  // Socios / Administradores e Historico de Alteracoes (P-SEG-2).
  partners: (id: string, signal?: AbortSignal) =>
    api.get<InsuredPartner[]>(`${base}/${id}/partners`, signal),
  addPartner: (id: string, input: InsuredPartnerInput) =>
    api.post<InsuredPartner>(`${base}/${id}/partners`, input),
  updatePartner: (id: string, partnerId: string, input: InsuredPartnerInput) =>
    api.patch<InsuredPartner>(`${base}/${id}/partners/${partnerId}`, input),
  removePartner: (id: string, partnerId: string) =>
    api.delete<void>(`${base}/${id}/partners/${partnerId}`),
  changeHistory: (id: string, params: InsuredChangeParams = {}, signal?: AbortSignal) =>
    requestPage<InsuredChangeEntry>(
      `${base}/${id}/change-history${query({ ...params })}`,
      signal,
    ),
};
